import React from "react"
import { useIntl } from "react-intl"
import { formatNumber, toPercentage } from "helpers/numbers"
import { CURRENT_COLOR, PREVIOUS_COLOR } from "../../constants"
import { IntlShape } from "react-intl/lib"
import { isDefined } from "helpers/objects"

type TooltipDataProps = {
	sale: number | null
	date: Date | null
	percentageGrowth?: number | null
	variant: "current" | "previous"
	isPenetrationMetric?: boolean
}

const enum className {
	positive = "font-green",
	negative = "font-red",
}

const TooltipData = ({
	sale,
	date,
	percentageGrowth,
	variant,
	isPenetrationMetric = false,
}: TooltipDataProps) => {
	const intl = useIntl()

	if (!isDefined(sale)) {
		return null
	}

	return (
		<div className="tooltip-data">
			<div
				className="data-period-color"
				style={{ backgroundColor: `${getColor(variant)}` }}
			></div>
			<div className="data-period-date">{date}</div>
			<div className="data-period-sale">
				{renderValue(isPenetrationMetric, sale, intl)}
			</div>
			{isDefined(percentageGrowth) && (
				<div className={selectColor(percentageGrowth)}>
					({renderDetails(isPenetrationMetric, percentageGrowth, intl)})
				</div>
			)}
		</div>
	)
}

const getColor = (variant: TooltipDataProps["variant"]) =>
	variant === "current" ? CURRENT_COLOR : PREVIOUS_COLOR

const renderPercentage = (
	percentageGrowth: number,
	intl: IntlShape,
	decimals: number
) => {
	return toPercentage({
		value: percentageGrowth,
		decimals: decimals,
		withPercentageChar: true,
		intl,
	})
}

const renderDetails = (
	isPenetrationMetric: boolean,
	percentageGrowth: number,
	intl: IntlShape
) => {
	return isPenetrationMetric
		? formatNumber(percentageGrowth, intl) + " pts"
		: renderPercentage(percentageGrowth, intl, 1)
}

const renderValue = (
	isPenetrationMetric: boolean,
	sale: number,
	intl: IntlShape
) => {
	return isPenetrationMetric
		? renderPercentage(sale, intl, 2)
		: formatNumber(sale, intl)
}

const selectColor = (percentageGrowth?: TooltipDataProps["percentageGrowth"]) =>
	percentageGrowth && percentageGrowth > 0
		? className.positive
		: className.negative

export default TooltipData
